import React, {FC, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import styled, {createGlobalStyle} from 'styled-components';
import {CSSTransition} from 'react-transition-group';

const GlobalTransitions = createGlobalStyle`
  .nib-overlay-enter {
    opacity: 0.01;
  }

  .nib-overlay-enter-active {
    opacity: 1;
    transition: opacity .3s cubic-bezier(0.550, 0.085, 0.680, 0.530);
  }

  .nib-overlay-exit {
    opacity: 1;
  }

  .nib-overlay-exit-active {
    opacity: 0.01;
    transition: opacity .3s cubic-bezier(0.550, 0.085, 0.680, 0.530);
  }
`;

export const backgrounds = {
  light: 'rgba(255,255,255,.9)',
  dark: 'rgba(67,67,67,.9)'
};

// Do not try to prevent the `mode` prop leaking to the DOM by destructuring props.
// It causes closeOnClick to break (this.el is undefined)
export const OverlayComponent = styled.div<OverlayProps>`
  position: fixed;
  overflow: auto;
  z-index: 1;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: ${props => (props.mode === 'light' ? backgrounds.light : backgrounds.dark)};
`;

const validModes = ['light', 'dark'] as const;
type Mode = (typeof validModes)[number];

export interface OverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  visible?: boolean;
  mode?: Mode;
  closeOnEsc?: boolean;
  closeOnClick?: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
}

const Overlay: FC<OverlayProps> = ({visible = false, mode = 'dark', closeOnEsc = true, closeOnClick = true, onClose, children, ...otherProps}) => {
  const ref = useRef<HTMLDivElement>();

  function handleClick(event: React.MouseEvent<HTMLDivElement>) {
    if (closeOnClick && event.target === ref.current) {
      onClose && onClose();
    }
  }

  useEffect(() => {
    function handleKeydown(event: KeyboardEvent) {
      if (visible && closeOnEsc && event.key === 'Escape') {
        onClose && onClose();
      }
    }

    function enablePageScrolling() {
      document.body.style.overflow = '';
    }

    function disablePageScrolling() {
      document.body.style.overflow = 'hidden';
    }

    if (visible) {
      disablePageScrolling();
      document.addEventListener('keydown', handleKeydown);
    } else {
      enablePageScrolling();
      document.removeEventListener('keydown', handleKeydown);
    }

    return function cleanup() {
      enablePageScrolling();
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [visible, closeOnEsc, onClose]);

  return (
    <div>
      <GlobalTransitions />

      <CSSTransition timeout={300} in={visible} classNames="nib-overlay" mountOnEnter unmountOnExit nodeRef={ref}>
        <OverlayComponent {...otherProps} ref={ref as React.MutableRefObject<HTMLDivElement>} onClick={handleClick} mode={mode}>
          {children}
        </OverlayComponent>
      </CSSTransition>
    </div>
  );
};

Overlay.propTypes = {
  visible: PropTypes.bool,
  mode: PropTypes.oneOf(validModes),
  closeOnEsc: PropTypes.bool,
  closeOnClick: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node
};

export default Overlay;
